import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Badge,
  Card,
  CardBody,
} from "reactstrap";
import { AlternativePagination } from "../../components/Common/Table";
import "../../global.css";
import AWSS3Uploader from "../../components/Common/S3uploader";
import {
  GetProducts,
  AddProduct,
  UpdateProduct,
  DeleteProduct,
  GetCategories,
  GetVariants,
} from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from 'react-toastify';
import Breadcrumbs from "../../components/Common/Breadcrumb";

const Products = () => {
  document.title = "Product Master | Susipizza";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [variantTypes, setVariantTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search and Filter State
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterVeg, setFilterVeg] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Modal Form State
  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [buttonText, setButtonText] = useState("Save");

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [veg, setVeg] = useState("true"); // true = Veg, false = Non-Veg
  const [status, setStatus] = useState("active"); // active, inactive
  const [sku, setSku] = useState("");

  // Variant Configuration
  const [variantEnabled, setVariantEnabled] = useState("false"); // "true" or "false"
  const [variantTypeId, setVariantTypeId] = useState("");
  const [variantValueIds, setVariantValueIds] = useState([]); // selected values

  // Addons (e.g. "Extra Cheese +₹40") — array of { name, price }
  const [addons, setAddons] = useState([]);

  // Delete Modal
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);

  const toggle = () => {
    if (modal) {
      setName("");
      setCategoryId("");
      setDescription("");
      setImage("");
      setVeg("true");
      setStatus("active");
      setSku("");
      setVariantEnabled("false");
      setVariantTypeId("");
      setVariantValueIds([]);
      setAddons([]);
      setEditingId(null);
      setButtonText("Save");
    }
    setModal(!modal);
  };

  const handleAddonAdd = () => {
    setAddons([...addons, { name: "", price: "" }]);
  };

  const handleAddonChange = (index, field, value) => {
    const updated = [...addons];
    updated[index] = { ...updated[index], [field]: value };
    setAddons(updated);
  };

  const handleAddonRemove = (index) => {
    setAddons(addons.filter((_, i) => i !== index));
  };

  const handleVariantTypeChange = (e) => {
    const selectedTypeId = e.target.value;
    setVariantTypeId(selectedTypeId);
    setVariantValueIds([]); // Reset selected values
  };

  const handleCheckboxChange = (value, checked) => {
    if (checked) {
      setVariantValueIds([...variantValueIds, value]);
    } else {
      setVariantValueIds(variantValueIds.filter(v => v !== value));
    }
  };

  const startEdit = (row) => {
    setEditingId(row.productId || row.id);
    setName(row.name);
    setCategoryId(row.categoryId);
    setDescription(row.description || "");
    setImage(row.image || "");
    setVeg(row.veg ? "true" : "false");
    setStatus(row.status || "active");
    setSku(row.sku || "");
    setVariantEnabled(row.variantEnabled ? "true" : "false");
    setVariantTypeId(row.variantTypeId || "");
    setVariantValueIds(row.variantValueIds || []);
    setAddons(
      (row.addons || []).map((a) => ({ id: a.id, name: a.name, price: a.price.toString() }))
    );
    setButtonText("Update");
    setModal(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!categoryId) {
      toast.error("Category is required");
      return;
    }
    if (variantEnabled === "true" && !variantTypeId) {
      toast.error("Variant type is required when variants are enabled");
      return;
    }
    if (variantEnabled === "true" && variantValueIds.length === 0) {
      toast.error("Please select at least one variant value");
      return;
    }
    for (const a of addons) {
      if (!a.name.trim()) {
        toast.error("Every addon needs a name");
        return;
      }
      const p = parseFloat(a.price);
      if (isNaN(p) || p < 0) {
        toast.error(`Price for addon '${a.name}' must be zero or greater`);
        return;
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        name: name,
        categoryId: categoryId,
        description: description,
        image: image,
        veg: veg === "true",
        status: status,
        variantEnabled: variantEnabled === "true",
        variantTypeId: variantEnabled === "true" ? variantTypeId : null,
        variantValueIds: variantEnabled === "true" ? variantValueIds : [],
        addons: addons.map((a) => ({ id: a.id, name: a.name.trim(), price: parseFloat(a.price) })),
      };

      if (editingId) {
        await UpdateProduct(token, editingId, payload);
        toast.success("Product updated successfully");
      } else {
        await AddProduct(token, payload);
        toast.success("Product created successfully");
      }
      toggle();
      loadData();
    } catch (err) {
      console.error("Save product error:", err.response?.data || err);
      const errorMsg = err.response?.data?.error?.message || err.response?.data?.error || "Error occurred while saving product";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteProduct(token, targetId);
      toast.success("Product deleted successfully");
      loadData();
    } catch (err) {
      console.error(err);
      toast.error("Error occurred while deleting product");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {
        search: search,
        categoryId: filterCategory,
        veg: filterVeg,
        status: filterStatus,
      };
      const [prodResp, catResp, varResp] = await Promise.all([
        GetProducts(filters),
        GetCategories(),
        GetVariants(),
      ]);

      setProducts(prodResp.data.data || prodResp.data || []);
      setCategories(catResp.data.data || catResp.data || []);
      setVariantTypes(varResp.data.data || varResp.data || []);
    } catch (err) {
      console.error("Error loading products components:", err);
      toast.error("Failed to load page data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterVeg, filterStatus]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      loadData();
    }
  };

  // Find variant type values by ID
  const getSelectedVariantTypeValues = () => {
    const vt = variantTypes.find(v => (v.variantId || v.id) === variantTypeId);
    return vt ? vt.values || [] : [];
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => (c.categoryId || c.id) === id);
    return cat ? cat.category : id;
  };

  const getVariantTypeName = (id) => {
    if (!id) return "-";
    const vt = variantTypes.find(v => (v.variantId || v.id) === id);
    return vt ? vt.name : id;
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      cell: (row) => (
        row.image ? (
          <img src={row.image} alt={row.name} width="50" height="50" style={{ objectFit: "cover", borderRadius: "4px" }} />
        ) : (
          <div className="bg-light d-flex align-items-center justify-content-center" style={{ width: 50, height: 50, borderRadius: "4px" }}>
            <i className="mdi mdi-pizza-thin text-muted" style={{ fontSize: "20px" }}></i>
          </div>
        )
      ),
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Product Name</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">SKU</span>,
      selector: (row) => row.sku,
      width: "120px",
    },
    {
      name: <span className="font-weight-bold fs-13">Category</span>,
      selector: (row) => getCategoryName(row.categoryId),
      sortable: true,
      width: "140px",
    },
    {
      name: <span className="font-weight-bold fs-13">Variant Type</span>,
      selector: (row) => getVariantTypeName(row.variantTypeId),
      width: "140px",
    },
    {
      name: <span className="font-weight-bold fs-13">Veg</span>,
      cell: (row) => (
        row.veg ? (
          <Badge color="soft-success" className="badge-soft-success">Veg</Badge>
        ) : (
          <Badge color="soft-danger" className="badge-soft-danger">Non-Veg</Badge>
        )
      ),
      width: "100px",
    },
    {
      name: <span className="font-weight-bold fs-13">Status</span>,
      cell: (row) => (
        row.status === "active" ? (
          <Badge color="success" className="badge-success">Active</Badge>
        ) : (
          <Badge color="secondary" className="badge-secondary">Inactive</Badge>
        )
      ),
      width: "100px",
    },
    {
      name: <span className="font-weight-bold fs-13">Created Date</span>,
      selector: (row) => {
        if (!row.createdAt) return "-";
        const dateObj = row.createdAt._seconds ? new Date(row.createdAt._seconds * 1000) : new Date(row.createdAt);
        return dateObj.toLocaleDateString();
      },
      width: "120px",
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      width: "110px",
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-icon btn-outline-primary"
            onClick={() => startEdit(row)}
            title="Edit Product"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-icon btn-outline-danger"
            onClick={() => handleDeleteClick(row.productId || row.id)}
            title="Remove Product"
          >
            <i className="mdi mdi-trash-can-outline"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Catalog" breadcrumbItem="Product Master" />

          {/* Filter Panel */}
          <Card className="mb-4">
            <CardBody>
              <Row className="g-3">
                <Col md="4">
                  <FormGroup className="mb-0">
                    <Label for="searchInput">Search Products</Label>
                    <div className="d-flex gap-2">
                      <Input
                        id="searchInput"
                        type="text"
                        placeholder="Search by product name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                      />
                      <Button color="primary" onClick={loadData}>Search</Button>
                    </div>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-0">
                    <Label for="filterCategory">Category</Label>
                    <Input
                      id="filterCategory"
                      type="select"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.categoryId || cat.id}>
                          {cat.category}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-0">
                    <Label for="filterVeg">Type</Label>
                    <Input
                      id="filterVeg"
                      type="select"
                      value={filterVeg}
                      onChange={(e) => setFilterVeg(e.target.value)}
                    >
                      <option value="">All Types</option>
                      <option value="true">Veg</option>
                      <option value="false">Non-Veg</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-0">
                    <Label for="filterStatus">Status</Label>
                    <Input
                      id="filterStatus"
                      type="select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="2" className="d-flex align-items-end justify-content-end">
                  <Button color="primary" className="w-100" onClick={toggle}>
                    Add Product
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <AlternativePagination columns={columns} data={products} />
        </Container>
      </div>

      {/* Save Modal */}
      <Modal isOpen={modal} toggle={toggle} size="lg" centered>
        <ModalHeader toggle={toggle}>
          {editingId ? "Edit Product" : "Add Product"}
        </ModalHeader>
        <ModalBody className="modal-color">
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="prodName">Product Name *</Label>
                <Input
                  id="prodName"
                  placeholder="e.g. Margherita Pizza"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="prodCategory">Category *</Label>
                <Input
                  id="prodCategory"
                  type="select"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.categoryId || cat.id}>
                      {cat.category}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="prodDesc">Description</Label>
                <Input
                  id="prodDesc"
                  type="textarea"
                  rows="3"
                  placeholder="Product description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormGroup>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="prodVeg">Veg / Non-Veg</Label>
                    <Input
                      id="prodVeg"
                      type="select"
                      value={veg}
                      onChange={(e) => setVeg(e.target.value)}
                    >
                      <option value="true">Veg</option>
                      <option value="false">Non-Veg</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="prodStatus">Active Status</Label>
                    <Input
                      id="prodStatus"
                      type="select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label for="skuField">SKU</Label>
                    <Input
                      id="skuField"
                      type="text"
                      value={editingId ? sku : "Auto-generated on save"}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label>Product Image</Label>
                <AWSS3Uploader onUploadComplete={(url) => setImage(url)} folder="products" />
                {image && (
                  <div className="mt-2 text-center border p-2 rounded bg-light">
                    <img src={image} alt="Preview" style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain" }} />
                  </div>
                )}
              </FormGroup>

              {/* Variant Configuration */}
              <div className="border rounded p-3 bg-light">
                <h5 className="font-size-14 mb-3">Variant Configuration</h5>
                <FormGroup>
                  <Label for="variantEnabled">Enable Variants?</Label>
                  <Input
                    id="variantEnabled"
                    type="select"
                    value={variantEnabled}
                    onChange={(e) => {
                      setVariantEnabled(e.target.value);
                      if (e.target.value === "false") {
                        setVariantTypeId("");
                        setVariantValueIds([]);
                      }
                    }}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </Input>
                </FormGroup>

                {variantEnabled === "true" && (
                  <React.Fragment>
                    <FormGroup>
                      <Label for="variantTypeSelect">Variant Type</Label>
                      <Input
                        id="variantTypeSelect"
                        type="select"
                        value={variantTypeId}
                        onChange={handleVariantTypeChange}
                      >
                        <option value="">Select Variant Type</option>
                        {variantTypes.map((vt) => (
                          <option key={vt.id} value={vt.variantId || vt.id}>
                            {vt.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    {variantTypeId && (
                      <FormGroup className="mb-0">
                        <Label>Select Variant Values</Label>
                        <div className="border rounded p-3 bg-white" style={{ maxHeight: "150px", overflowY: "auto" }}>
                          {getSelectedVariantTypeValues().length === 0 ? (
                            <span className="text-muted fs-12">No values available for this type.</span>
                          ) : (
                            getSelectedVariantTypeValues().map((val, idx) => (
                              <div key={idx} className="form-check mb-2">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`chk-${idx}`}
                                  checked={variantValueIds.includes(val)}
                                  onChange={(e) => handleCheckboxChange(val, e.target.checked)}
                                />
                                <Label className="form-check-label" for={`chk-${idx}`}>
                                  {val}
                                </Label>
                              </div>
                            ))
                          )}
                        </div>
                      </FormGroup>
                    )}
                  </React.Fragment>
                )}
              </div>

              {/* Addons Configuration */}
              <div className="border rounded p-3 bg-light mt-3">
                <h5 className="font-size-14 mb-3">
                  Addons (e.g. "Extra Cheese")
                </h5>
                {addons.map((a, idx) => (
                  <Row key={idx} className="align-items-center mb-2">
                    <Col md="6">
                      <Input
                        type="text"
                        placeholder="Addon name"
                        value={a.name}
                        onChange={(e) => handleAddonChange(idx, "name", e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <Input
                        type="number"
                        placeholder="Price (₹)"
                        value={a.price}
                        onChange={(e) => handleAddonChange(idx, "price", e.target.value)}
                      />
                    </Col>
                    <Col md="2">
                      <Button color="outline-danger" size="sm" onClick={() => handleAddonRemove(idx)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button color="outline-primary" size="sm" onClick={handleAddonAdd}>
                  + Add Addon
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {buttonText}
          </Button>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this product? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Products;
